/**
 * usePurchaseOrderForm Hook
 *
 * Extracts all form logic from PurchaseOrderFormModal.
 * Handles state, mutations, and actions for purchase order creation/editing.
 *
 * @see US-8.15 - Formulaire Creation Commande
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { DateRange } from '@/components/ui';
import {
  usePurchaseOrder,
  useCreatePurchaseOrder,
  useUpdatePurchaseOrder,
  useSendPurchaseOrder,
  useCancelPurchaseOrder,
  useDuplicatePurchaseOrder,
} from './usePurchaseOrders';
import { useActiveSuppliers } from '@/features/suppliers/hooks';
import { PurchaseOrderStatus } from '@bstock/shared';
import type { AddOrderItemDto } from '@bstock/shared';
import type { ModalMode } from '@/hooks';
import type { OrderLineItem } from '../components/OrderItemsTable';

interface UsePurchaseOrderFormParams {
  isOpen: boolean;
  mode: ModalMode;
  orderId: string | null;
  onClose: () => void;
  onOpenEdit: (id: string) => void;
}

export function usePurchaseOrderForm({
  isOpen,
  mode,
  orderId,
  onClose,
  onOpenEdit,
}: UsePurchaseOrderFormParams) {
  const navigate = useNavigate();
  const isEditMode = mode === 'edit' || mode === 'view';

  // Form state
  const [supplierId, setSupplierId] = useState<string>('');
  const [expectedDateRange, setExpectedDateRange] = useState<DateRange>({ start: null, end: null });
  const [notes, setNotes] = useState<string>('');
  const [items, setItems] = useState<OrderLineItem[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Confirmation modals state
  const [showSupplierChangeConfirm, setShowSupplierChangeConfirm] = useState(false);
  const [pendingSupplierId, setPendingSupplierId] = useState<string | null>(null);
  const [showSendConfirm, setShowSendConfirm] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // Fetch existing order first (needed to determine if we need suppliers)
  const { data: existingOrder, isLoading: orderLoading } = usePurchaseOrder(
    isEditMode && orderId ? orderId : null
  );

  // Only fetch suppliers when creating a new order or editing a DRAFT order
  // For view mode or non-draft orders, supplier info is already in the order data
  const needsSuppliers = isOpen && (
    mode === 'create' ||
    (mode === 'edit' && existingOrder?.status === PurchaseOrderStatus.DRAFT)
  );
  const { data: suppliers = [], isLoading: suppliersLoading } = useActiveSuppliers({
    enabled: needsSuppliers,
  });

  // Mutations
  const createMutation = useCreatePurchaseOrder();
  const updateMutation = useUpdatePurchaseOrder();
  const sendMutation = useSendPurchaseOrder();
  const cancelMutation = useCancelPurchaseOrder();
  const duplicateMutation = useDuplicatePurchaseOrder();

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSupplierId('');
      setExpectedDateRange({ start: null, end: null });
      setNotes('');
      setItems([]);
      setHasUnsavedChanges(false);
    }
  }, [isOpen]);

  // Initialize form from existing order
  useEffect(() => {
    if (existingOrder && isEditMode) {
      setSupplierId(existingOrder.supplierId);
      const existingDate = existingOrder.expectedDate ? new Date(existingOrder.expectedDate) : null;
      setExpectedDateRange({ start: existingDate, end: existingDate });
      setNotes(existingOrder.notes ?? '');
      setItems(
        existingOrder.items.map((item) => ({
          id: item.id,
          catalogItemId: item.catalogItemId ?? '',
          rawProductId: item.rawProductId,
          rawProductName: item.rawProductName,
          packagingName: item.packagingName,
          conversionToRecipeUnit: item.conversionToRecipeUnit,
          recipeUnitSymbol: '',
          quantity: item.quantity,
          unitPriceExclTax: item.unitPriceExclTax,
        }))
      );
      setHasUnsavedChanges(false);
    }
  }, [existingOrder, isEditMode]);

  // Supplier options
  const supplierOptions = useMemo(
    () => suppliers.map((s) => ({ value: s.id, label: s.name })),
    [suppliers]
  );

  // Is form valid?
  const isFormValid = useMemo(() => {
    return Boolean(supplierId) && items.length > 0;
  }, [supplierId, items]);

  // Handle supplier change
  const handleSupplierChange = useCallback(
    (newSupplierId: string) => {
      if (items.length > 0 && newSupplierId !== supplierId) {
        setPendingSupplierId(newSupplierId);
        setShowSupplierChangeConfirm(true);
      } else {
        setSupplierId(newSupplierId);
        setHasUnsavedChanges(true);
      }
    },
    [items.length, supplierId]
  );

  const confirmSupplierChange = useCallback(() => {
    if (pendingSupplierId) {
      setSupplierId(pendingSupplierId);
      setItems([]);
      setHasUnsavedChanges(true);
    }
    setShowSupplierChangeConfirm(false);
    setPendingSupplierId(null);
  }, [pendingSupplierId]);

  const cancelSupplierChange = useCallback(() => {
    setShowSupplierChangeConfirm(false);
    setPendingSupplierId(null);
  }, []);

  // Handle items change
  const handleItemsChange = useCallback((newItems: OrderLineItem[]) => {
    setItems(newItems);
    setHasUnsavedChanges(true);
  }, []);

  // Handle expected date change
  const handleExpectedDateChange = useCallback((range: DateRange) => {
    setExpectedDateRange(range);
    setHasUnsavedChanges(true);
  }, []);

  // Handle notes change
  const handleNotesChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNotes(e.target.value);
    setHasUnsavedChanges(true);
  }, []);

  // Save order (create or update)
  const handleSave = useCallback(async () => {
    if (!supplierId) return;

    const itemsDto: AddOrderItemDto[] = items.map((item) => ({
      catalogItemId: item.catalogItemId,
      quantity: item.quantity,
    }));

    try {
      if (isEditMode && existingOrder) {
        // Single API call: header + items
        await updateMutation.mutateAsync({
          id: existingOrder.id,
          data: {
            expectedDate: expectedDateRange.start?.toISOString() || null,
            notes: notes || null,
            items: itemsDto,
          },
        });

        setHasUnsavedChanges(false);
      } else {
        // Create new order with items
        const newOrder = await createMutation.mutateAsync({
          supplierId,
          expectedDate: expectedDateRange.start?.toISOString() || null,
          notes: notes || null,
          items: itemsDto,
        });

        setHasUnsavedChanges(false);
        onOpenEdit(newOrder.id);
      }
    } catch {
      // Error handled by mutation
    }
  }, [
    isEditMode,
    existingOrder,
    supplierId,
    expectedDateRange,
    notes,
    items,
    createMutation,
    updateMutation,
    onOpenEdit,
  ]);

  // Send order
  const handleSend = useCallback(async () => {
    setShowSendConfirm(false);

    if (!isEditMode || !existingOrder) {
      await handleSave();
      return;
    }

    try {
      await sendMutation.mutateAsync(existingOrder.id);
      onClose();
    } catch {
      // Error handled by mutation
    }
  }, [isEditMode, existingOrder, handleSave, sendMutation, onClose]);

  // Handle send click
  const handleSendClick = useCallback(async () => {
    if (!isFormValid) return;

    if (hasUnsavedChanges || !isEditMode) {
      await handleSave();
    }

    setShowSendConfirm(true);
  }, [isFormValid, hasUnsavedChanges, isEditMode, handleSave]);

  const cancelSendConfirm = useCallback(() => {
    setShowSendConfirm(false);
  }, []);

  // Handle cancel order
  const handleCancelOrder = useCallback(async () => {
    if (!existingOrder) return;

    try {
      await cancelMutation.mutateAsync(existingOrder.id);
      setShowCancelConfirm(false);
      onClose();
    } catch {
      // Error handled by mutation
    }
  }, [existingOrder, cancelMutation, onClose]);

  const openCancelConfirm = useCallback(() => {
    setShowCancelConfirm(true);
  }, []);

  const closeCancelConfirm = useCallback(() => {
    setShowCancelConfirm(false);
  }, []);

  // Handle duplicate order
  const handleDuplicateOrder = useCallback(async () => {
    if (!existingOrder) return;

    try {
      const result = await duplicateMutation.mutateAsync(existingOrder.id);
      onOpenEdit(result.order.id);
    } catch {
      // Error handled by mutation
    }
  }, [existingOrder, duplicateMutation, onOpenEdit]);

  // Handle receive navigation
  const handleReceive = useCallback(() => {
    if (!existingOrder) return;
    navigate(`/achats/receptions/create?fromOrder=true&orderId=${existingOrder.id}`);
  }, [existingOrder, navigate]);

  // Computed: modal title
  const modalTitle = useMemo(() => {
    if (isEditMode && existingOrder) {
      return existingOrder.orderNumber;
    }
    return 'Nouvelle commande';
  }, [isEditMode, existingOrder]);

  // Computed: status flags
  const statusFlags = useMemo(() => {
    if (!existingOrder) {
      return {
        isDraft: true,
        isSent: false,
        isPartiallyReceived: false,
        isReceived: false,
        isCancelled: false,
        canReceive: false,
      };
    }

    const isSent = existingOrder.status === PurchaseOrderStatus.SENT;
    const isPartiallyReceived = existingOrder.status === PurchaseOrderStatus.PARTIALLY_RECEIVED;
    const isReceived = existingOrder.status === PurchaseOrderStatus.RECEIVED;
    const isCancelled = existingOrder.status === PurchaseOrderStatus.CANCELLED;
    const isDraft = existingOrder.status === PurchaseOrderStatus.DRAFT;
    const canReceive = isSent || isPartiallyReceived;

    return { isDraft, isSent, isPartiallyReceived, isReceived, isCancelled, canReceive };
  }, [existingOrder]);

  // Computed: reception progress
  const receptionProgress = useMemo(() => {
    if (!existingOrder) {
      return { totalOrdered: 0, totalReceived: 0, completedLines: 0, totalLines: 0 };
    }

    let totalOrdered = 0;
    let totalReceived = 0;
    let completedLines = 0;

    for (const item of existingOrder.items) {
      totalOrdered += item.quantity;
      totalReceived += item.quantityReceived;
      if (item.quantityReceived >= item.quantity) {
        completedLines++;
      }
    }

    return {
      totalOrdered,
      totalReceived,
      completedLines,
      totalLines: existingOrder.items.length,
    };
  }, [existingOrder]);

  // Mutation states
  const isSaving = createMutation.isPending || updateMutation.isPending;
  const isSending = sendMutation.isPending;
  const isCancelling = cancelMutation.isPending;
  const isDuplicating = duplicateMutation.isPending;

  return {
    // Mode
    isEditMode,

    // Form state
    supplierId,
    expectedDateRange,
    notes,
    items,
    hasUnsavedChanges,

    // Data
    existingOrder,
    supplierOptions,
    suppliersLoading,
    orderLoading,

    // Derived state
    modalTitle,
    isFormValid,
    statusFlags,
    receptionProgress,

    // Modal state
    showSupplierChangeConfirm,
    pendingSupplierId,
    showSendConfirm,
    showCancelConfirm,

    // Mutation states
    isSaving,
    isSending,
    isCancelling,
    isDuplicating,

    // Handlers
    handleSupplierChange,
    confirmSupplierChange,
    cancelSupplierChange,
    handleItemsChange,
    handleExpectedDateChange,
    handleNotesChange,
    handleSave,
    handleSendClick,
    handleSend,
    cancelSendConfirm,
    handleCancelOrder,
    openCancelConfirm,
    closeCancelConfirm,
    handleDuplicateOrder,
    handleReceive,
  };
}
